import { Controller } from 'stimulus'
import { FetchRequest } from '@rails/request.js'

/**
 * @class RemoteFilteredSelectController
 * @memberof Controllers
 *
 * @example
 * @see shared/_remote_filtered_select.html.haml
 */
export default class extends Controller {
  static targets = [ 'formField', 'popup', 'query', 'results', 'resultTemplate', 'spinner' ]

  /**
   * @function Controllers.RemoteFilteredSelectController.show
   * @memberof Controllers
   * @description Shows query and results selection UI
   * @param {Event} event - Event from user
   */
  show(event) {
    if (this._navigatingInput(event) || event.shiftKey) {
      return
    }

    this.popupTarget.classList.remove(this._hiddenCssClassName)
    this.queryTarget.value = ''
    this.queryTarget.focus()
    this._listenForClickAway()
  }

  /**
   * @function Controllers.RemoteFilteredSelectController.hide
   * @memberof Controllers
   * @description Hides query and results selection UI
   * @param {Event} event - Event from user
   */
  hide(event) {
    this.popupTarget.classList.add(this._hiddenCssClassName)
    this._stopListeningForClickAway()
  }

  /**
   * @function Controllers.RemoteFilteredSelectController.filter
   * @memberof Controllers
   * @description Queries remote API and displays results
   * @param {Event} event - Event from user
   */
  async filter(event) {
    if (this._navigatingInput(event)) {
      return
    }

    const query = { [this._remoteQueryVar]: event.target.value }
    const request = new FetchRequest('get', this._remoteUrl, { contentType: 'application/json', query: query, responseKind: 'json' })

    this._showSpinner()
    const response = await request.perform()

    if (response.ok) {
      this._hideSpinner()

      response.json.then(results => {
        this._clearResults()
        results.forEach(result => this._addResultItem(result))
      })
    }
  }

  /**
   * @function Controllers.RemoteFilteredSelectController.select
   * @memberof Controllers
   * @description User picked search result
   * @param {Event} event - Event from user
   */
  select(event) {
    if (event.type === 'click' || event.code === 'Enter') {
      this._applySelection(event.target.textContent)
      this.hide()
    }
  }

  /**
   * @function Controllers.RemoteFilteredSelectController.fuzzySelect
   * @memberof Controllers
   * @description Select only search result as desired
   * @param {Event} event - Event from user
   */
  fuzzySelect(event) {
    event = event.detail.originalEvent || event // unwrap debounced event

    if (event.code !== 'Enter' || this.resultsTarget.childElementCount !== 1) {
      return
    }

    event.preventDefault()
    this._applySelection(this.resultsTarget.firstChild.textContent)
    this.hide()
  }

  /**
   * @function Controllers.RemoteFilteredSelectController.navigate
   * @memberof Controllers
   * @description Move between query input and search results using the keyboard
   * @param {Event} event - Event from user
   */
  navigate(event) {
    event = event.detail.originalEvent || event // unwrap debounced event
    const code = event.code
    const shifting = event.shiftKey
    const movingDown = (code == 'Tab' && !shifting) || code == 'ArrowDown'
    const movingUp   = (code == 'Tab' && shifting) || code == 'ArrowUp'
    const hiding     = code == 'Escape'

    if (movingDown || movingUp || hiding) {
      event.preventDefault()
    }

    if (movingDown) {
      this._focusNextFrom(event.target)
    } else if (movingUp) {
      this._focusPreviousFrom(event.target)
    } else if (hiding) {
      this.hide()
    }
  }

  //
  // "Private" methods
  //

  /**
   * @function Controllers.RemoteFilteredSelectController._navigatingInput
   * @memberof Controllers
   * @description Is the current event key a "navigation" key?
   * @param {Event} event - Event from user
   * @private
   * @return {boolean}
   */
  _navigatingInput(event) {
    event = event.detail.originalEvent || event // unwrap debounced event
    return [ 'ArrowDown', 'ArrowUp', 'Tab' ].includes(event.code)
  }

  /**
   * @function Controllers.RemoteFilteredSelectController._applySelection
   * @memberof Controllers
   * @description Set the formFieldTarget value to passed in value
   * @param {String} value - Value to apply
   * @private
   */
  _applySelection(value) {
    this.formFieldTarget.value = value
  }

  /**
   * @function Controllers.RemoteFilteredSelectController._focusNextFrom
   * @memberof Controllers
   * @description Move focus to next UI element
   * @param {Node} el - Element to base focus traversal from
   * @private
   */
  _focusNextFrom(el) {
    if (el === this.queryTarget) {
      this.resultsTarget.firstElementChild.focus()
    } else {
      const next = el.nextElementSibling || this.queryTarget
      next.focus()
    }
  }

  /**
   * @function Controllers.RemoteFilteredSelectController._focusPreviousFrom
   * @memberof Controllers
   * @description Move focus to previous UI element
   * @param {Node} el - Element to base focus traversal from
   * @private
   */
  _focusPreviousFrom(el) {
    if (el === this.queryTarget) {
      this.resultsTarget.lastElementChild.focus()
    } else {
      const prev = el.previousElementSibling || this.queryTarget
      prev.focus()
    }
  }

  /**
   * @function Controllers.RemoteFilteredSelectController._listenForClickAway
   * @memberof Controllers
   * @description Attach listener to detect clicks outside this component
   * @private
   */
  _listenForClickAway() {
    this.boundHandleOtherInput = this._handleOtherInput.bind(this)
    document.addEventListener('click', this.boundHandleOtherInput)
  }

  /**
   * @function Controllers.RemoteFilteredSelectController._stopListeningForClickAway
   * @memberof Controllers
   * @description Detach listener to detect clicks outside this component
   * @private
   */
  _stopListeningForClickAway() {
    document.removeEventListener('click', this.boundHandleOtherInput)
  }

  /**
   * @function Controllers.RemoteFilteredSelectController._handleOtherInput
   * @memberof Controllers
   * @description If the user clicks outside then close the component
   * @param {Event} event - Event from user
   * @private
   */
  _handleOtherInput(event) {
    if (this._localEvent(event)) { return }

    this.hide()
  }

  /**
   * @function Controllers.RemoteFilteredSelectController._localEvent
   * @memberof Controllers
   * @description Did the event occur within this component?
   * @param {Event} event - Event from user
   * @return {boolean}
   * @private
   */
  _localEvent(event) {
    return event.target.closest(this._elementCssSelector) == this.element
  }

  /**
   * @function Controllers.RemoteFilteredSelectController._clearResults
   * @memberof Controllers
   * @description Clear the result of all items
   * @private
   */
  _clearResults() {
    this.resultsTarget.innerHTML = ''
  }

  /**
   * @function Controllers.RemoteFilteredSelectController._addResultItem
   * @memberof Controllers
   * @description Append value to results list
   * @param {string} value - Value to add to results list
   * @private
   */
  _addResultItem(value) {
    if (value) {
      const resultItem = this.resultTemplateTarget.content.firstElementChild.cloneNode(true)
      resultItem.textContent = value
      this.resultsTarget.appendChild(resultItem)
    }
  }

  /**
   * @function Controllers.RemoteFilteredSelectController._hideSpinner
   * @memberof Controllers
   * @description Hide activity spinner
   * @private
   */
  _hideSpinner() {
    this.spinnerTarget.classList.remove(this._spinnerCssClassName)
  }

  /**
   * @function Controllers.RemoteFilteredSelectController._showSpinner
   * @memberof Controllers
   * @description Show activity spinner
   * @private
   */
  _showSpinner() {
    this.spinnerTarget.classList.add(this._spinnerCssClassName)
  }

  /**
   * @function Controllers.RemoteFilteredSelectController._hiddenCssClassName
   * @memberof Controllers
   * @return {string}
   * @private
   */
  get _hiddenCssClassName() {
    return this.data.get('cssHide')
  }

  /**
   * @function Controllers.RemoteFilteredSelectController._spinnerCssClassName
   * @memberof Controllers
   * @return {string}
   * @private
   */
  get _spinnerCssClassName() {
    return this.data.get('cssSpinner')
  }

  /**
   * @function Controllers.RemoteFilteredSelectController._remoteUrl
   * @memberof Controllers
   * @return {string}
   * @private
   */
  get _remoteUrl() {
    return this.data.get('remote-url')
  }

  /**
   * @function Controllers.RemoteFilteredSelectController._remoteQueryVar
   * @memberof Controllers
   * @return {string}
   * @private
   */
  get _remoteQueryVar() {
    return this.data.get('remote-query-var')
  }

  /**
   * @function Controllers.RemoteFilteredSelectController._elementCssSelector
   * @memberof Controllers
   * @return {string}
   * @private
   */
  get _elementCssSelector() {
    return Array.from(this.element.classList.values()).map(name => `.${name}`).reduce((memo, name) => memo += name)
  }
}
